/**
 * Marketplace Payments List Page
 */
import { useState, type MouseEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Chip,
  Menu,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Payments as RevenueIcon,
  ReceiptLong as CountIcon,
  CheckCircle as CompletedIcon,
  HourglassEmpty as PendingIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { PageHeader, DataTable, Column, StatsCard } from '../../components/common';
import {
  useGetMarketplacePaymentsQuery,
  useGetMarketplacePaymentStatsQuery,
  useUpdateMarketplacePaymentStatusMutation,
} from '../../store/api';
import { usePermissions } from '../../hooks/usePermissions';
import { MarketplacePaymentListItem, MarketplacePaymentStatus } from '../../types/marketplace';
import {
  getAllowedNextPaymentStatuses,
  splitPaymentStatusesForMenu,
  formatPaymentStatusUpdateError,
  type PaymentStatus,
} from '../../utils/paymentStatusTransitions';
import { formatCurrency } from '../../utils/pricing';
import moment from 'moment';

const PAYMENT_STATUS_COLOR: Record<
  MarketplacePaymentStatus,
  'default' | 'info' | 'primary' | 'success' | 'error' | 'warning'
> = {
  PENDING: 'warning',
  PROCESSING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
  REFUNDED: 'default',
  PARTIALLY_REFUNDED: 'default',
};

const STATUS_OPTIONS: MarketplacePaymentStatus[] = [
  'PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'REFUNDED', 'PARTIALLY_REFUNDED',
];

export const MarketplacePaymentsListPage = ({ embedded = false }: { embedded?: boolean } = {}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const statusLabel = (s: MarketplacePaymentStatus) => t(`payments.status.${s}`);
  const methodLabel = (m: 'CASH' | 'FAWRY') => t(`payments.method.${m}`);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [method, setMethod] = useState('');

  const { data, isLoading, error } = useGetMarketplacePaymentsQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    status: status || undefined,
    payment_method: method || undefined,
  });

  const { data: statsResp } = useGetMarketplacePaymentStatsQuery();
  const stats = statsResp?.data;

  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('manage_marketplace');
  const [updatePaymentStatus] = useUpdateMarketplacePaymentStatusMutation();
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  // status menu -> dialog (mirrors the service payments list)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuPayment, setMenuPayment] = useState<MarketplacePaymentListItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPayment, setDialogPayment] = useState<MarketplacePaymentListItem | null>(null);
  const [newStatus, setNewStatus] = useState<PaymentStatus | ''>('');
  const [adminNotes, setAdminNotes] = useState('');
  const [failureReason, setFailureReason] = useState('');

  const openStatusMenu = (e: MouseEvent<HTMLElement>, p: MarketplacePaymentListItem) => {
    setMenuAnchor(e.currentTarget);
    setMenuPayment(p);
  };
  const closeStatusMenu = () => {
    setMenuAnchor(null);
    setMenuPayment(null);
  };
  const chooseStatus = (s: PaymentStatus) => {
    setDialogPayment(menuPayment);
    setNewStatus(s);
    setAdminNotes('');
    setFailureReason('');
    setDialogOpen(true);
    closeStatusMenu();
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setDialogPayment(null);
    setNewStatus('');
  };
  const submitStatus = async () => {
    if (!dialogPayment || !newStatus) return;
    setUpdatingId(dialogPayment.id);
    try {
      await updatePaymentStatus({
        id: dialogPayment.id,
        status: newStatus,
        ...(adminNotes.trim() && { admin_notes: adminNotes.trim() }),
        ...(newStatus === 'FAILED' && failureReason.trim() && { failure_reason: failureReason.trim() }),
      }).unwrap();
      toast.success(t('payments.toast.updated', 'Payment status updated'));
      closeDialog();
    } catch (err: unknown) {
      toast.error(formatPaymentStatusUpdateError(err));
    } finally {
      setUpdatingId(null);
    }
  };

  const columns: Column<MarketplacePaymentListItem>[] = [
    { id: 'payment_number', label: t('payments.cols.paymentNumber'), minWidth: 200 },
    {
      id: 'order_number',
      label: t('payments.cols.order'),
      minWidth: 170,
      render: (row) => (
        <Box
          onClick={() => navigate(`/marketplace/orders/${row.order_id}`)}
          sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          {row.order_number}
        </Box>
      ),
    },
    { id: 'user_name', label: t('payments.cols.customer'), minWidth: 180, render: (row) => row.user_name || row.user_email },
    {
      id: 'amount',
      label: t('payments.cols.amount'),
      minWidth: 110,
      align: 'right',
      render: (row) => formatCurrency(row.amount),
    },
    {
      id: 'payment_method',
      label: t('payments.cols.method'),
      minWidth: 90,
      render: (row) => methodLabel(row.payment_method),
    },
    {
      id: 'status',
      label: t('payments.cols.status'),
      minWidth: 150,
      render: (row) => {
        const chip = (
          <Chip label={statusLabel(row.status)} size="small" color={PAYMENT_STATUS_COLOR[row.status] ?? 'default'} />
        );
        const allowed = getAllowedNextPaymentStatuses(row.status);
        if (!canUpdate || allowed.length === 0) return chip;
        if (updatingId === row.id) return <CircularProgress size={20} thickness={4} />;
        return (
          <Box
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openStatusMenu(e, row);
            }}
            aria-label={t('payments.dialog.title', 'Change payment status')}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.25, cursor: 'pointer',
              border: 'none', background: 'none', p: 0, m: 0, font: 'inherit', color: 'inherit',
              borderRadius: 1, '&:hover': { opacity: 0.85 },
            }}
          >
            {chip}
            <KeyboardArrowDownIcon sx={{ fontSize: '1.125rem', color: 'text.secondary' }} />
          </Box>
        );
      },
    },
    {
      id: 'paid_at',
      label: t('payments.cols.paidAt'),
      minWidth: 150,
      render: (row) => (row.paid_at ? moment(row.paid_at).format('MMM DD, YYYY HH:mm') : '—'),
    },
    {
      id: 'created_at',
      label: t('payments.cols.created'),
      minWidth: 120,
      render: (row) => moment(row.created_at).format('MMM DD, YYYY'),
    },
  ];

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded && (
        <PageHeader
          title={`${t('nav.marketplace')} — ${t('payments.title')}`}
          breadcrumbs={[
            { label: t('nav.dashboard'), path: '/' },
            { label: t('nav.marketplace'), path: '/marketplace/categories' },
            { label: t('payments.title') },
          ]}
        />
      )}

      {stats && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <StatsCard title={t('payments.stats.totalRevenue')} value={formatCurrency(stats.revenue.total_revenue)} icon={<RevenueIcon />} color="success" />
          <StatsCard title={t('payments.stats.totalPayments')} value={stats.overview.total_payments} icon={<CountIcon />} color="primary" />
          <StatsCard title={t('payments.stats.completed')} value={stats.overview.completed} icon={<CompletedIcon />} color="info" />
          <StatsCard title={t('payments.stats.pending')} value={stats.overview.pending} icon={<PendingIcon />} color="warning" />
        </Box>
      )}

      <Card sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder={t('payments.filters.searchPlaceholder')}
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('payments.filters.status')}</InputLabel>
            <Select
              value={status}
              label={t('payments.filters.status')}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">{t('payments.filters.allStatuses')}</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('payments.filters.method')}</InputLabel>
            <Select
              value={method}
              label={t('payments.filters.method')}
              onChange={(e) => {
                setMethod(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">{t('payments.filters.allMethods')}</MenuItem>
              <MenuItem value="CASH">{methodLabel('CASH')}</MenuItem>
              <MenuItem value="FAWRY">{methodLabel('FAWRY')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Card>

      <DataTable
        columns={columns}
        data={data?.results || []}
        loading={isLoading}
        error={error ? t('common.error') : null}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.count || 0}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        emptyMessage={t('payments.empty')}
      />

      {/* Status change menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeStatusMenu}>
        {(() => {
          const allowed = menuPayment ? getAllowedNextPaymentStatuses(menuPayment.status) : [];
          const { progress, markFailed, refunds } = splitPaymentStatusesForMenu(allowed);
          const items: ReactElement[] = [];
          if (progress.length) {
            items.push(<ListSubheader key="h1">{t('payments.menu.progress', 'Move forward')}</ListSubheader>);
            progress.forEach((s) => items.push(
              <MenuItem key={s} onClick={() => chooseStatus(s)}>{statusLabel(s as MarketplacePaymentStatus)}</MenuItem>
            ));
          }
          if (markFailed.length) {
            items.push(<ListSubheader key="h2">{t('payments.menu.markFailed', 'Mark failed')}</ListSubheader>);
            markFailed.forEach((s) => items.push(
              <MenuItem key={s} onClick={() => chooseStatus(s)}>{statusLabel(s as MarketplacePaymentStatus)}</MenuItem>
            ));
          }
          if (refunds.length) {
            items.push(<ListSubheader key="h3">{t('payments.menu.refunds', 'Refund')}</ListSubheader>);
            refunds.forEach((s) => items.push(
              <MenuItem key={s} onClick={() => chooseStatus(s)}>{statusLabel(s as MarketplacePaymentStatus)}</MenuItem>
            ));
          }
          return items;
        })()}
      </Menu>

      {/* Status change dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('payments.dialog.title', 'Change Payment Status')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {dialogPayment && (
            <Box sx={{ mb: 2, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
              {dialogPayment.payment_number} —
              <Chip label={statusLabel(dialogPayment.status)} size="small" color={PAYMENT_STATUS_COLOR[dialogPayment.status] ?? 'default'} />
              →
              {newStatus && <Chip label={statusLabel(newStatus as MarketplacePaymentStatus)} size="small" color={PAYMENT_STATUS_COLOR[newStatus as MarketplacePaymentStatus] ?? 'default'} />}
            </Box>
          )}
          {newStatus === 'FAILED' && (
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label={t('payments.dialog.failureReason', 'Failure reason')}
              value={failureReason}
              onChange={(e) => setFailureReason(e.target.value)}
            />
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('payments.dialog.adminNotes', 'Admin notes (optional)')}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={submitStatus}
            disabled={!newStatus || updatingId !== null}
          >
            {updatingId !== null ? t('common.loading') : t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketplacePaymentsListPage;
